/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import R from 'ramda'
import {
  ExportComponent,
  UtilityUnitSelection,
  getUtilityNameMap,
  getUnitNameMap,
  rowHeaderRender,
  exportDataComponent,
  getApiTargetInfo,
  getUnits,
} from 'core'
import {
  utilityHeader,
  unitHeader,
  modifierCol,
  modifiedDateCol,
} from 'core/HeaderColTypeProps'
import { ErrorContext, SpinContext } from 'context'
import {
  Content,
  CriteriaRow,
  Filler,
  FullHeightSizer,
  Title,
  Label,
} from 'core/screen-util'
import { dateCol, strCol } from '@integec/grid-tools/lib/cols'
import {
  Grid,
  defaultVirtualizedCellRender,
  virtualizedGridRenderer,
  freeEditEngine,
} from '@integec/grid-tools'
import {
  getIIFOOutage,
  saveIIFOOutage,
  deleteIIFOOutage,
  updateIIFOOutage,
  getAllIIFOOutage,
} from 'api/pgads'
import {
  Button,
  DatePicker,
  Empty,
  Input,
  Popconfirm,
  Select,
  message,
} from 'antd'
import { DATE_FORMAT_API } from 'enum'
import moment from 'moment'

const title = 'IIFO Outage'

const IIFO = ({ apiTargetInfo, units, utilityNameMap, unitNameMap }) => {
  const { spinning, setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)
  const [iifoData, setIIFOData] = useState([])
  const [popModal, setPopModal] = useState(false)
  const [fuzzyFilter, setFuzzyFilter] = useState(null)
  const [modalInfo, setModalInfo] = useState({
    utility: '',
    unit: '',
    eventstart: '',
    eventend: '',
  })
  const [pnodeId, setPnodeId] = useState([])
  const [shortName, setShortName] = useState([])
  const [selectedRows, setRowsSelected] = useState([])
  const [unitShortNameMap, setUnitShortNameMap] = useState({})
  const [unitPnodeIdMap, setUnitPnodeIdMap] = useState({})
  const GridRef = useRef()
  const [editInfo, setEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )

  useEffect(() => {
    setUnitShortNameMap(unitShortNameList(units))
    setUnitPnodeIdMap(pnodeIdNameList(units))
  }, [units])

  const validateData = data => {
    if (!data.utility) {
      message.warn('Utility is required.')
      return false
    }
    if (!data.unit) {
      message.warn('Unit is required.')
      return false
    }
    if (!data.eventstart) {
      message.warn('IIFO Outage Period Start Date cannot be blank.')
      return false
    }
    if (moment(data.eventstart).isAfter(moment().startOf('D'))) {
      message.warn('IIFO Outage Period Start Date cannot be in the future.')
      return false
    }
    if (moment(data.eventend).isSameOrBefore(moment(data.eventstart))) {
      message.warn(
        'IIFO Outage Period End Date cannot be less than or equal to IIFO Outage Period Start.'
      )
      return false
    }
    const eventEndCheck = data.eventend
      ? moment(data.eventend)
      : moment().add(10000, 'days')
    const overlapData = iifoData
      .filter(record => !record.updated)
      .filter(record => {
        const isUtilityMatch = record.utility === data.utility
        const isUnitMatch = record.unit === data.unit
        const isEventStartValid = moment(record.eventstart).isBefore(
          eventEndCheck
        )
        const isEventEndValid =
          record.eventend == null ||
          moment(record.eventend).isAfter(data.eventstart)
        return (
          isUtilityMatch && isUnitMatch && isEventStartValid && isEventEndValid
        )
      })

    if (overlapData.length > 0) {
      message.error(
        'Unable to save data due to invalid overlap with another IIFO outage.'
      )
      return false
    }
    return true
  }

  const validateDataEachOther = updatedRecords => {
    const eventEndCheck = record =>
      record.eventend ? moment(record.eventend) : moment().add(10000, 'days')

    const hasOverlap = updatedRecords.some((recordA, index, array) =>
      array.some((recordB, innerIndex) => {
        if (index === innerIndex) return false // Skip comparing the same record
        return (
          recordA.utility === recordB.utility &&
          recordA.unit === recordB.unit &&
          moment(recordA.eventstart).isBefore(eventEndCheck(recordB)) &&
          (!recordA.eventend ||
            moment(recordA.eventend).isAfter(recordB.eventstart))
        )
      })
    )

    if (hasOverlap) {
      message.error(
        'Unable to save data due to invalid overlap with another IIFO outage.'
      )
      return false
    }

    return true // No overlaps detected
  }

  const loadData = () => {
    setSpinning(true)
    getAllIIFOOutage(apiTargetInfo).fork(setError, data => {
      setSpinning(false)
      const effectiveData = data.map(d => ({
        ...d,
        shortname: unitShortNameMap[`${d.utility}${d.unit}`],
        pnodeId: unitPnodeIdMap[`${d.utility}${d.unit}`],
      }))
      setIIFOData(effectiveData)
    })
  }

  const loadDataByResourcePTID = () => {
    if (pnodeId.length <= 0) {
      message.warn('Please select Resource PTID')
      return
    }
    const effectiveUnit = units
      .filter(u => pnodeId.includes(u.pnodeId))
      .map(u => u.utility + '-' + u.unit)

    setSpinning(true)
    getIIFOOutage(apiTargetInfo)(effectiveUnit).fork(setError, data => {
      setSpinning(false)
      const effectiveData = data.map(d => ({
        ...d,
        shortname: unitShortNameMap[`${d.utility}${d.unit}`],
        pnodeId: unitPnodeIdMap[`${d.utility}${d.unit}`],
      }))
      setIIFOData(effectiveData)
    })
  }

  const loadDataByShortName = () => {
    if (shortName.length <= 0) {
      message.warn('Please select Short Name')
      return
    }
    const effectiveUnit = units
      .filter(u => shortName.includes(u.shortName))
      .map(u => u.utility + '-' + u.unit)
    setSpinning(true)
    getIIFOOutage(apiTargetInfo)(effectiveUnit).fork(setError, data => {
      setSpinning(false)
      const effectiveData = data.map(d => ({
        ...d,
        shortname: unitShortNameMap[`${d.utility}${d.unit}`],
        pnodeId: unitPnodeIdMap[`${d.utility}${d.unit}`],
      }))
      setIIFOData(effectiveData)
    })
  }

  const onSaveData = (data, hide) => {
    const requiredData = {
      utility: data.utility,
      unit: data.unit,
      eventstart: data.eventstart,
      eventend: data.eventend,
    }
    if (!validateData(requiredData)) return
    setSpinning(true)
    saveIIFOOutage(apiTargetInfo)([requiredData]).fork(setError, () => {
      setSpinning(false)
      if (!hide) {
        setPopModal(false)
      }
      setModalInfo({})
      message.success('IIFO Outage data saved successfully')
      loadData()
    })
  }

  const onDelete = () => {
    if (selectedRows.length <= 0) {
      message.warn('Please select a row to delete')
    } else {
      setSpinning(true)
      deleteIIFOOutage(apiTargetInfo)(selectedRows).fork(setError, () => {
        message.info('Data deleted successfully.')
        setSpinning(false)
        loadData()
      })
    }
  }

  const onUpdate = () => {
    const saveData = freeEditEngine.applyEdits({
      data: iifoData?.filter(d => d.updated),
      editInfo,
    })
    if (saveData.length <= 0) {
      message.error('Please, update data')
      return
    }
    const allValid = saveData.every(d => validateData(d))
    if (!allValid) {
      return
    }
    const updatedRecordValidation = validateDataEachOther(saveData)
    if (!updatedRecordValidation) {
      return
    }
    setSpinning(true)
    updateIIFOOutage(apiTargetInfo)(saveData).fork(setError, () => {
      setSpinning(false)
      message.success('IIFO Outage data saved successfully')
      setEditInfo(freeEditEngine.generateInitialEditInfo())
      loadData()
    })
  }

  const editRow = editInfo => (data, setEditInfo, setData) => {
    const modData = data.map(d => {
      if (editInfo.updatedMap.get(d)) {
        return {
          ...editInfo.updatedMap.get(d),
          modificationInfo: undefined,
          updated: true,
        }
      } else {
        return d
      }
    })

    let editData = editInfo.updated
    editData = editData.map(d => ({
      ...d,
      modificationInfo: undefined,
    }))
    setEditInfo({ ...editInfo, updated: editData })
    setData(modData)
  }

  const dateEditor = props => {
    const { data, header, rowIndex, style, getInputProps, cancelEdit } = props
    const { onChange, value } = getInputProps({ refKey: 'innerRef' })
    return (
      <DatePicker.MonthPicker
        value={value ? moment(value) : null}
        onChange={date => {
          const newEditInfo = freeEditEngine.updateRow({
            editInfo,
            currentRow: data[rowIndex],
            editedRow: {
              ...data[rowIndex],
              [header.ident]: date
                ? moment(date)
                    .endOf('month')
                    .format(header.dataFormat)
                : null,
            },
          })
          editRow(newEditInfo)(iifoData, setEditInfo, setIIFOData)
          data[rowIndex][header.ident] = date
            ? date.format(header.dataFormat)
            : null
          onChange({
            target: { value: date ? date.format(header.dataFormat) : date },
          })
        }}
        style={{
          ...style,
          padding: `0px`,
          margin: `0px`,
          paddingLeft: `10px`,
          borderTop: `0px solid #fff`,
        }}
        format={header.displayFormat}
        size="small"
        onOpenChange={status => {
          if (!status) cancelEdit()
        }}
        allowClear={header.allowClear}
        suffixIcon={null}
      />
    )
  }

  const cellRender = props => {
    const { gridToolProps, reactVirtualizedProps } = props
    const { style } = reactVirtualizedProps
    const type = gridToolProps.headers[reactVirtualizedProps.columnIndex].type
    return defaultVirtualizedCellRender({
      ...props,
      style,
      editRender:
        type === 'date' || type === 'date-time' ? dateEditor : undefined,
    })
  }

  const headerWithFormatter = header.map(header =>
    header.ident === 'utility'
      ? {
          ...header,
          dataFormatter: ({ value }) =>
            utilityNameMap[value]
              ? value + ' - ' + utilityNameMap[value]
              : value,
          sortIndexGetter: ({ rowData }) =>
            utilityNameMap[rowData.utility] || rowData.utility,
        }
      : header.ident === 'unit'
      ? {
          ...header,
          dataFormatter: ({ rowData }) =>
            unitNameMap[`${rowData.utility}${rowData.unit}`]
              ? rowData.unit +
                ' - ' +
                unitNameMap[`${rowData.utility}${rowData.unit}`]
              : rowData.unit,
          sortIndexGetter: ({ rowData }) =>
            unitNameMap[`${rowData.utility}${rowData.unit}`] || rowData.unit,
        }
      : header
  )

  const isEditableComp = ({ header, rowData }) => {
    const { ident } = header
    if (
      ident === 'eventend' &&
      ((rowData.eventend &&
        moment(rowData.eventend).isAfter(moment().format(DATE_FORMAT_API))) ||
        !rowData.eventend ||
        rowData.updated)
    )
      return true
    return false
  }

  return (
    <>
      <div
        style={{
          backgroundColor: '#f0f2f5',
          marginLeft: '20px',
          marginRight: '20px',
        }}
      >
        {/* <UnitSelection /> */}
        <CriteriaRow
          style={{
            display: 'flex',
            width: '100%',
          }}
        >
          <Title
            style={{
              marginRight: '15px',
              marginBottom: '5px',
              minWidth: '170px',
            }}
          >
            {title}
          </Title>
          <Button
            onClick={loadData}
            type="primary"
            icon="database"
            style={{ marginLeft: '10px' }}
          >
            Retrieve
          </Button>

          <Button
            onClick={() => {
              setPopModal(true)
            }}
            icon="plus-circle"
            style={{ backgroundColor: '#e6f7ff', marginLeft: '10px' }}
          >
            Insert
          </Button>
          <Button
            onClick={onUpdate}
            icon="edit"
            type="primary"
            disabled={editInfo.updated.length <= 0}
            style={{ marginLeft: '10px' }}
          >
            Update
          </Button>
          <Popconfirm
            title={
              <span>
                The selected IIFO record shall be permanently deleted.
                <br />
                Select Continue to proceed or Cancel.
              </span>
            }
            okText="Continue"
            cancelText="Cancel"
            onConfirm={onDelete}
            placement="topRight"
            okType="danger"
          >
            <Button
              type="danger"
              disabled={selectedRows.length <= 0}
              icon="delete"
              style={{ marginLeft: '10px' }}
            >
              Delete
            </Button>
          </Popconfirm>
          <Filler />
          <Input.Search
            placeholder="Filter"
            value={fuzzyFilter}
            style={{
              marginLeft: '10px',
              marginRight: '10px',
              flex: '0 0 200px',
            }}
            onChange={e => setFuzzyFilter(e.target.value)}
          />

          <ExportComponent
            getData={_ => exportDataComponent(GridRef, iifoData, fuzzyFilter)}
            data={iifoData}
            headers={headerWithFormatter}
            fileName={title}
            btnStyle={{ marginRight: '10px' }}
            disabled={!!(iifoData && iifoData.length <= 0)}
          />
        </CriteriaRow>
        <CriteriaRow
          style={{
            display: 'flex',
            width: '100%',
            paddingLeft: '200px',
          }}
        >
          <div>
            <Label style={{ display: 'inline', float: 'right' }}>
              Resource PTID :
            </Label>
          </div>
          <Select
            showSearch
            mode="multiple"
            placeholder="Resource PTID"
            style={{ marginLeft: '10px', width: '300px' }}
            value={pnodeId}
            onChange={value => {
              setPnodeId(value)
            }}
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            maxTagCount={3}
          >
            {[
              ...new Map(
                units
                  .filter(m => !!m.pnodeId?.trim()) // filters out null and undefined
                  .map(m => [m.pnodeId, m]) // dedupe by pnodeId
              ).values(),
            ]
              .slice()
              .sort((a, b) =>
                a.pnodeId?.trim().localeCompare(b.pnodeId?.trim())
              )
              .map(m => (
                <Select.Option value={m.pnodeId} key={m.pnodeId}>
                  {m.pnodeId}
                </Select.Option>
              ))}
          </Select>
          <Button
            onClick={loadDataByResourcePTID}
            type="primary"
            icon="database"
            style={{ marginLeft: '10px' }}
          >
            Retrieve By Resource PTID
          </Button>
          <div>
            <Label
              style={{ display: 'inline', float: 'right', marginLeft: '30px' }}
            >
              Unit Short Name :
            </Label>
          </div>
          <Select
            showSearch
            mode="multiple"
            placeholder="Unit Short Name"
            style={{ marginLeft: '10px', width: '300px' }}
            value={shortName}
            onChange={value => setShortName(value)}
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            maxTagCount={3}
          >
            {units
              .slice()
              .sort((a, b) => a.shortName.localeCompare(b.shortName))
              .map(m => (
                <Select.Option value={m.shortName} key={m.shortName}>
                  {m.shortName}
                </Select.Option>
              ))}
          </Select>
          <Button
            onClick={loadDataByShortName}
            type="primary"
            icon="database"
            style={{ marginLeft: '10px' }}
          >
            Retrieve By Short Name
          </Button>
        </CriteriaRow>
        {popModal && (
          <CriteriaRow css={{ marginTop: 10 }}>
            <EditModal
              modalInfo={modalInfo}
              setModalInfo={setModalInfo}
              handleSave={onSaveData}
              hide={() => setPopModal(false)}
              processing={spinning}
              unitShortNameMap={unitShortNameMap}
            />
          </CriteriaRow>
        )}
        <FullHeightSizer heightOffset={380}>
          {({ height, width }) =>
            iifoData && iifoData.length > 0 ? (
              <Content style={{ height: height - (popModal ? 70 : 0) + 20 }}>
                <Grid
                  ref={GridRef}
                  sortEnabled
                  data={iifoData}
                  headers={headerWithFormatter}
                  altBy={u => u.utility + u.unit}
                  altBgColor="#ebebeb"
                  render={virtualizedGridRenderer({
                    rowHeaderRender,
                    height: height - (popModal ? 70 : 0),
                    width: width - 120,
                    cellRender: cellRender,
                    selectionMode: 'single',
                    headerRowHeight: 30,
                  })}
                  initialSortOptions={[
                    dateCol({
                      ident: 'eventstart',
                      display: 'IIFO Outage Period Start Date',
                      isFilterable: true,
                      width: 180,
                      isKey: true,
                      sortOrder: 'asc',
                      dataFormat: DATE_FORMAT_API,
                      displayFormat: 'MMM-YYYY',
                    }),
                  ]}
                  fuzzyFilter={fuzzyFilter}
                  isEditable={isEditableComp}
                  editMode="cell"
                  onSelectionChange={rows => setRowsSelected(rows.selectedRows)}
                  selectionType="row"
                  selectionMode="single"
                />
              </Content>
            ) : (
              <Content style={{ height: '200px' }}>
                <Empty
                  imageStyle={{ paddingTop: '30px' }}
                  description={
                    iifoData && iifoData.length <= 0
                      ? 'No Data from Retrieve'
                      : 'Please click on retrieve button to retrieve data'
                  }
                />
              </Content>
            )
          }
        </FullHeightSizer>
      </div>
    </>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  units: getUnits(state),
  utilityNameMap: getUtilityNameMap(state),
  unitNameMap: getUnitNameMap(state),
})

export default connect(mapStateToProps)(IIFO)

const unitShortNameList = R.compose(
  R.fromPairs,
  R.map(a => [`${a.utility}${a.unit}`, a.shortName])
)

const pnodeIdNameList = R.compose(
  R.fromPairs,
  R.map(a => [`${a.utility}${a.unit}`, a.pnodeId])
)

const EditModal = ({ handleSave, hide, modalInfo, setModalInfo }) => {
  const onOk = show => {
    handleSave(modalInfo, show)
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '250px 250px 250px 250px 250px 200px',
        gap: 10,
      }}
    >
      <div>
        <Label style={{ display: 'inline', float: 'right' }}>
          Utility / Unit :
        </Label>
        <span style={{ color: 'red', marginRight: '2px', float: 'right' }}>
          *
        </span>
      </div>
      <UtilityUnitSelection
        style={{ gridColumnEnd: 'span 3' }}
        utility={modalInfo.utility}
        unit={modalInfo.unitId}
        onUnitChange={value => {
          setModalInfo({
            ...modalInfo,
            utility: value[0],
            unitId: value[1],
            unit: value[1].substr(3),
          })
        }}
      />
      <div />
      <div />
      <div>
        <Label style={{ display: 'inline', float: 'right' }}>
          IIFO Outage Period Start Date :
        </Label>
        <span style={{ color: 'red', marginRight: '2px', float: 'right' }}>
          *
        </span>
      </div>
      <DatePicker.MonthPicker
        allowClear={false}
        format="MMM-YYYY"
        value={
          modalInfo.eventstart
            ? moment(modalInfo.eventstart, DATE_FORMAT_API)
            : null
        }
        onChange={date =>
          setModalInfo({
            ...modalInfo,
            eventstart: moment(date)
              .startOf('month')
              .format(DATE_FORMAT_API),
          })
        }
      />

      <div>
        <Label style={{ display: 'inline', float: 'right' }}>
          IIFO Outage Period End Date :
        </Label>
      </div>
      <DatePicker.MonthPicker
        allowClear={false}
        format="MMM-YYYY"
        value={
          modalInfo.eventend
            ? moment(modalInfo.eventend, DATE_FORMAT_API)
            : null
        }
        onChange={date =>
          setModalInfo({
            ...modalInfo,
            eventend: moment(date)
              .endOf('month')
              .format(DATE_FORMAT_API),
          })
        }
      />

      <div
        style={{
          display: 'grid',
          gridColumnEnd: 'span 2',
          gridTemplateColumns: 'auto auto auto',
          gap: 10,
        }}
      >
        <Button type="primary" onClick={() => onOk(true)}>
          Add
        </Button>

        <Button type="primary" onClick={() => onOk(false)}>
          Add & Hide
        </Button>

        <Button type="danger" onClick={_ => hide()}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

export const header = [
  strCol({
    ident: 'pnodeId',
    display: 'Resource PTID',
    isFilterable: true,
    width: 120,
  }),
  utilityHeader,
  unitHeader,
  strCol({
    ident: 'shortname',
    display: 'Unit Short Name',
    isFilterable: true,
    width: 120,
  }),
  dateCol({
    ident: 'eventstart',
    display: 'IIFO Outage Period Start Date',
    isFilterable: true,
    width: 180,
    isKey: true,
    dataFormat: DATE_FORMAT_API,
    displayFormat: 'MMM-YYYY',
  }),
  dateCol({
    ident: 'eventend',
    display: 'IIFO Outage Period End Date ',
    isFilterable: true,
    width: 180,
    dataFormat: DATE_FORMAT_API,
    displayFormat: 'MMM-YYYY',
  }),
  modifierCol,
  modifiedDateCol,
].map(h => ({ ...h, ellipsis: true }))
